import { AiSummaryKind } from "./aiSummaryKinds";

const PDF_HORIZONTAL_MARGIN_POINTS = 34;
const PDF_CONTENT_TOP_POINTS = 44;
const PDF_CONTENT_BOTTOM_POINTS = 38;
const MAX_CAPTURE_SCALE = 2;
const MAX_CAPTURE_PIXELS = 20_000_000;
const MIN_PAGE_FILL_RATIO = 0.35;

export type PdfPageSlice = {
  start: number;
  height: number;
};

export type PdfProtectedRange = {
  start: number;
  end: number;
};

const moveCutBeforeProtectedRanges = (
  pageStart: number,
  plannedEnd: number,
  ranges: PdfProtectedRange[]
): number => {
  let adjustedEnd = plannedEnd;

  while (true) {
    let blockingRange: PdfProtectedRange | undefined;
    for (const range of ranges) {
      if (
        range.start > pageStart &&
        range.start < adjustedEnd &&
        adjustedEnd < range.end
      ) {
        blockingRange = range;
        break;
      }
    }

    if (!blockingRange) {
      return adjustedEnd;
    }
    adjustedEnd = blockingRange.start;
  }
};

export const calculatePdfCaptureHeight = (
  scrollHeight: number,
  rootTop: number,
  descendantBottoms: number[]
): number => {
  const renderedContentHeight = descendantBottoms.reduce(
    (maximumHeight, bottom) => {
      const height = bottom - rootTop;
      return Number.isFinite(height) && height > maximumHeight
        ? height
        : maximumHeight;
    },
    0
  );

  return Math.ceil(Math.max(scrollHeight, renderedContentHeight));
};

const PDF_FILE_NAMES: Partial<Record<AiSummaryKind, string>> = {
  health_overview: "ai-health-overview.pdf",
  rmr_summary: "ai-rmr-summary.pdf",
  rmr_inbody_summary: "ai-rmr-inbody-summary.pdf",
  amr_summary: "ai-amr-summary.pdf",
  progress_summary_1: "ai-progress-summary-1.pdf",
  progress_summary_2: "ai-progress-summary-2.pdf",
};

const PDF_TITLES: Partial<Record<AiSummaryKind, string>> = {
  health_overview: "HEALTH OVERVIEW",
  rmr_summary: "RMR SUMMARY",
  rmr_inbody_summary: "RMR + INBODY SUMMARY",
  amr_summary: "AMR SUMMARY",
  progress_summary_1: "PROGRESS SUMMARY 1",
  progress_summary_2: "PROGRESS REPORT 2",
};

export const getAiSummaryPdfFileName = (kind: AiSummaryKind): string =>
  PDF_FILE_NAMES[kind] ?? "ai-summary.pdf";

export const getAiSummaryPdfTitle = (kind: AiSummaryKind): string =>
  PDF_TITLES[kind] ?? "AI SUMMARY";

export const calculatePdfPageSlices = (
  documentHeight: number,
  maximumPageHeight: number,
  breakBoundaries: number[],
  protectedRanges: PdfProtectedRange[] = []
): PdfPageSlice[] => {
  if (documentHeight <= 0 || maximumPageHeight <= 0) {
    return [];
  }

  const boundaries = Array.from(
    new Set(
      breakBoundaries
        .map((boundary) => Math.round(boundary))
        .filter((boundary) => boundary > 0 && boundary < documentHeight)
    )
  ).sort((left, right) => left - right);
  const ranges = protectedRanges
    .map((range) => ({
      start: Math.max(0, Math.round(range.start)),
      end: Math.min(documentHeight, Math.round(range.end)),
    }))
    .filter(
      (range) =>
        range.end > range.start && range.end - range.start <= maximumPageHeight
    )
    .sort((left, right) => left.start - right.start || right.end - left.end);
  const isInsideProtectedRange = (position: number): boolean =>
    ranges.some((range) => range.start < position && position < range.end);
  const slices: PdfPageSlice[] = [];
  let start = 0;

  while (start < documentHeight) {
    const idealEnd = Math.min(start + maximumPageHeight, documentHeight);
    if (idealEnd === documentHeight) {
      slices.push({ start, height: documentHeight - start });
      break;
    }

    const minimumUsefulEnd = start + maximumPageHeight * MIN_PAGE_FILL_RATIO;
    const preferredEnd = boundaries.reduce(
      (candidate, boundary) =>
        boundary >= minimumUsefulEnd &&
        boundary <= idealEnd &&
        !isInsideProtectedRange(boundary)
          ? boundary
          : candidate,
      0
    );
    const plannedEnd = preferredEnd > start ? preferredEnd : idealEnd;

    // If the planned cut intersects a block that fits on one page, move the
    // cut above that block. Repeat for overlapping/nested blocks so a section
    // heading stays with its first card instead of being orphaned.
    const end = moveCutBeforeProtectedRanges(start, plannedEnd, ranges);

    slices.push({ start, height: end - start });
    start = end;
  }

  return slices;
};

const waitForImages = async (element: HTMLElement): Promise<void> => {
  const images = Array.from(element.querySelectorAll("img"));
  await Promise.all(
    images.map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }

          image.addEventListener("load", () => resolve(), { once: true });
          image.addEventListener("error", () => resolve(), { once: true });
        })
    )
  );
};

const waitForStableLayout = async (): Promise<void> => {
  await new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });
};

export const exportAiSummaryToPdf = async (
  element: HTMLElement,
  kind: AiSummaryKind,
  backgroundColor: string
): Promise<void> => {
  if (element.scrollWidth === 0) {
    throw new Error("The summary content is not available for export.");
  }

  if (document.fonts) {
    await document.fonts.ready;
  }

  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);

  const captureWidth = element.scrollWidth;
  const captureElement = element.cloneNode(true) as HTMLElement;
  captureElement.dataset.aiSummaryPdfCapture = "true";
  captureElement.style.position = "absolute";
  captureElement.style.zIndex = "0";
  captureElement.style.left = "-10000px";
  captureElement.style.top = "0";
  captureElement.style.width = `${captureWidth}px`;
  captureElement.style.height = "auto";
  captureElement.style.maxHeight = "none";
  captureElement.style.overflow = "visible";
  document.body.appendChild(captureElement);

  const { canvas, breakBoundaries, protectedRanges } = await (async () => {
    try {
      await waitForImages(captureElement);
      await waitForStableLayout();

      const rootBounds = captureElement.getBoundingClientRect();
      const captureHeight = calculatePdfCaptureHeight(
        captureElement.scrollHeight,
        rootBounds.top,
        [
          rootBounds.bottom,
          ...Array.from(captureElement.querySelectorAll<HTMLElement>("*")).map(
            (child) => child.getBoundingClientRect().bottom
          ),
        ]
      );
      if (captureHeight === 0) {
        throw new Error("The summary content is not available for export.");
      }

      // html2canvas lays the report out in its own iframe. Leave enough room for
      // small font-metric differences, then crop to the cloned document's
      // measured height after that iframe has loaded its fonts and images.
      const renderViewportHeight = Math.ceil(captureHeight * 1.5 + 100);
      const deviceScale = Math.max(window.devicePixelRatio || 1, 1);
      const pixelBudgetScale = Math.sqrt(
        MAX_CAPTURE_PIXELS / (captureWidth * renderViewportHeight)
      );
      const scale = Math.max(
        0.75,
        Math.min(MAX_CAPTURE_SCALE, deviceScale, pixelBudgetScale)
      );
      let clonedCaptureHeight = captureHeight;
      let clonedBreakOffsets: number[] = [];
      let clonedProtectedRanges: PdfProtectedRange[] = [];
      const renderedCanvas = await html2canvas(captureElement, {
        backgroundColor,
        scale,
        useCORS: true,
        logging: false,
        width: captureWidth,
        height: renderViewportHeight,
        windowWidth: captureWidth,
        windowHeight: renderViewportHeight,
        onclone: (clonedDocument, clonedReport) => {
          clonedReport.style.position = "absolute";
          clonedReport.style.zIndex = "0";
          clonedReport.style.left = "0";
          clonedReport.style.top = "0";
          clonedReport.style.width = `${captureWidth}px`;
          clonedReport.style.height = "auto";
          clonedReport.style.maxHeight = "none";
          clonedReport.style.overflow = "visible";
          clonedDocument.documentElement.style.height = `${renderViewportHeight}px`;
          clonedDocument.body.style.height = `${renderViewportHeight}px`;
          clonedDocument.body.style.overflow = "visible";

          const clonedBounds = clonedReport.getBoundingClientRect();
          clonedCaptureHeight = calculatePdfCaptureHeight(
            clonedReport.scrollHeight,
            clonedBounds.top,
            [
              clonedBounds.bottom,
              ...Array.from(
                clonedReport.querySelectorAll<HTMLElement>("*")
              ).map((child) => child.getBoundingClientRect().bottom),
            ]
          );
          clonedBreakOffsets = [
            ...Array.from(
              clonedReport.querySelectorAll<HTMLElement>("[data-pdf-break]")
            ).map(
              (block) => block.getBoundingClientRect().bottom - clonedBounds.top
            ),
            ...Array.from(
              clonedReport.querySelectorAll<HTMLElement>(
                "[data-pdf-break-before]"
              )
            ).map(
              (block) => block.getBoundingClientRect().top - clonedBounds.top
            ),
          ];

          const keepTogetherBlocks = Array.from(
            clonedReport.querySelectorAll<HTMLElement>("[data-pdf-break]")
          );
          const sectionLeadBlocks = Array.from(
            clonedReport.querySelectorAll<HTMLElement>("section")
          ).flatMap((section) => {
            const firstBreakBlock =
              section.querySelector<HTMLElement>("[data-pdf-break]");
            if (!firstBreakBlock) {
              return [];
            }

            const sectionBounds = section.getBoundingClientRect();
            const firstBlockBounds = firstBreakBlock.getBoundingClientRect();
            return [
              {
                start: sectionBounds.top - clonedBounds.top,
                end: firstBlockBounds.bottom - clonedBounds.top,
              },
            ];
          });
          clonedProtectedRanges = [
            ...keepTogetherBlocks.map((block) => {
              const blockBounds = block.getBoundingClientRect();
              return {
                start: blockBounds.top - clonedBounds.top,
                end: blockBounds.bottom - clonedBounds.top,
              };
            }),
            ...sectionLeadBlocks,
          ];
        },
      });

      // Canvas dimensions are rounded down, but html2canvas draws using the
      // original scale. Using width / captureWidth makes cuts drift in long reports.
      const sourcePixelsPerCssPixel = scale;
      const requiredCanvasHeight = Math.ceil(
        clonedCaptureHeight * sourcePixelsPerCssPixel
      );
      if (requiredCanvasHeight > renderedCanvas.height) {
        throw new Error(
          "The summary content exceeded the PDF capture area. Please try again."
        );
      }
      const croppedCanvas = document.createElement("canvas");
      croppedCanvas.width = renderedCanvas.width;
      croppedCanvas.height = requiredCanvasHeight;
      const croppedContext = croppedCanvas.getContext("2d");
      if (!croppedContext) {
        throw new Error("The browser could not prepare the PDF image.");
      }
      croppedContext.drawImage(
        renderedCanvas,
        0,
        0,
        renderedCanvas.width,
        requiredCanvasHeight,
        0,
        0,
        renderedCanvas.width,
        requiredCanvasHeight
      );

      return {
        canvas: croppedCanvas,
        breakBoundaries: clonedBreakOffsets.map(
          (offset) => offset * sourcePixelsPerCssPixel
        ),
        protectedRanges: clonedProtectedRanges.map((range) => ({
          start: range.start * sourcePixelsPerCssPixel,
          end: range.end * sourcePixelsPerCssPixel,
        })),
      };
    } finally {
      captureElement.remove();
    }
  })();

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const printableWidth = pageWidth - PDF_HORIZONTAL_MARGIN_POINTS * 2;
  const printableHeight =
    pageHeight - PDF_CONTENT_TOP_POINTS - PDF_CONTENT_BOTTOM_POINTS;
  const sourcePixelsPerPoint = canvas.width / printableWidth;
  const pageHeightInSourcePixels = Math.max(
    1,
    Math.floor(printableHeight * sourcePixelsPerPoint)
  );
  const slices = calculatePdfPageSlices(
    canvas.height,
    pageHeightInSourcePixels,
    breakBoundaries,
    protectedRanges
  );
  const reportTitle = getAiSummaryPdfTitle(kind);

  slices.forEach((slice, pageIndex) => {
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = slice.height;
    const context = pageCanvas.getContext("2d");
    if (!context) {
      throw new Error("The browser could not prepare the PDF image.");
    }

    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    context.drawImage(
      canvas,
      0,
      slice.start,
      canvas.width,
      slice.height,
      0,
      0,
      canvas.width,
      slice.height
    );

    if (pageIndex > 0) {
      pdf.addPage();
    }

    pdf.setDrawColor(225, 227, 229);
    pdf.setLineWidth(0.5);
    pdf.line(
      PDF_HORIZONTAL_MARGIN_POINTS,
      31,
      pageWidth - PDF_HORIZONTAL_MARGIN_POINTS,
      31
    );
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(0, 36, 87);
    pdf.text("PNOE", PDF_HORIZONTAL_MARGIN_POINTS, 23);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(109, 112, 117);
    pdf.text(reportTitle, pageWidth - PDF_HORIZONTAL_MARGIN_POINTS, 23, {
      align: "right",
    });

    pdf.addImage(
      pageCanvas.toDataURL("image/png"),
      "PNG",
      PDF_HORIZONTAL_MARGIN_POINTS,
      PDF_CONTENT_TOP_POINTS,
      printableWidth,
      slice.height / sourcePixelsPerPoint,
      undefined,
      "FAST"
    );

    const footerY = pageHeight - 19;
    pdf.setDrawColor(225, 227, 229);
    pdf.line(
      PDF_HORIZONTAL_MARGIN_POINTS,
      footerY - 9,
      pageWidth - PDF_HORIZONTAL_MARGIN_POINTS,
      footerY - 9
    );
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(109, 112, 117);
    pdf.text(
      "AI-generated clinical summary",
      PDF_HORIZONTAL_MARGIN_POINTS,
      footerY
    );
    pdf.text(
      `Page ${pageIndex + 1} of ${slices.length}`,
      pageWidth - PDF_HORIZONTAL_MARGIN_POINTS,
      footerY,
      { align: "right" }
    );
  });

  pdf.save(getAiSummaryPdfFileName(kind));
};
